/**
 * Inventory toolbar — the @ui list pattern (see ListPageRecipe): a search `Input`
 * in the toolbar start, then a "Filters" `Popover` holding the @ui `FilterBuilder`
 * (field → operator → value rows) and an Export button in the end. The seven
 * inventory filters (Type · Status · Condition · Owner · Location · Supplier ·
 * Model) are `select` fields; the server only supports equality, so each is
 * locked to the `is` operator. Owner/Location/Supplier/Model options are fetched
 * live. State is owned by the parent list section.
 */
import { useMemo, useState } from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Input, Button, Badge, Popover, PopoverTrigger, PopoverContent } from '@vnetwork/ui/primitives';
import { FilterBuilder, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import { useOwners, useLocations, useSuppliers, useItemModels, useRacks } from '@/api/capacities';
import {
  ANY,
  EMPTY_FILTERS,
  TYPE_OPTIONS,
  STATUS_OPTIONS,
  CONDITION_OPTIONS,
  type FilterOption,
  type ItemFilters,
} from './list-configs/item-filter-fields';

export interface ItemsToolbarProps {
  filters: ItemFilters;
  onFiltersChange: (next: ItemFilters) => void;
  query: string;
  onQueryChange: (value: string) => void;
  /** Resolved category — scopes the Model options to the selected category. */
  category: string;
  onExport: () => void;
  exportDisabled: boolean;
}

const OPTION_LIMIT = 50;
const IS_ONLY: FilterOption[] = [{ value: 'is', label: 'is' }];

export function ItemsToolbar({
  filters,
  onFiltersChange,
  query,
  onQueryChange,
  category,
  onExport,
  exportDisabled,
}: ItemsToolbarProps) {
  const [open, setOpen] = useState(false);
  const owners = useOwners();
  const locations = useLocations({ page_size: OPTION_LIMIT });
  const suppliers = useSuppliers({ page_size: OPTION_LIMIT });
  const models = useItemModels({ category, page_size: OPTION_LIMIT });
  const racks = useRacks({ page_size: OPTION_LIMIT });

  const opt = <T,>(rows: T[] | undefined, map: (r: T) => FilterOption): FilterOption[] =>
    (rows ?? []).map(map);

  const fields: FilterField[] = useMemo(
    () => [
      { value: 'item_type', label: 'Type', input: 'select', operators: IS_ONLY, options: TYPE_OPTIONS },
      { value: 'status', label: 'Status', input: 'select', operators: IS_ONLY, options: STATUS_OPTIONS },
      { value: 'condition', label: 'Condition', input: 'select', operators: IS_ONLY, options: CONDITION_OPTIONS },
      { value: 'owner', label: 'Owner', input: 'select', operators: IS_ONLY, options: opt(owners.data?.results, (o) => ({ value: o.value, label: o.label })) },
      { value: 'location_id', label: 'Location', input: 'select', operators: IS_ONLY, options: opt(locations.data?.results, (l) => ({ value: l.id, label: `${l.name} (${l.type})` })) },
      { value: 'supplier_id', label: 'Supplier', input: 'select', operators: IS_ONLY, options: opt(suppliers.data?.results, (s) => ({ value: s.id, label: s.name })) },
      { value: 'model_id', label: 'Model', input: 'select', operators: IS_ONLY, options: opt(models.data?.results, (m) => ({ value: m.id, label: m.name })) },
      { value: 'rack_id', label: 'Rack', input: 'select', operators: IS_ONLY, options: opt(racks.data?.results, (r) => ({ value: r.id, label: `${r.code} — ${r.name}` })) },
    ],
    [owners.data, locations.data, suppliers.data, models.data, racks.data],
  );

  // Active (non-ANY) filters → FilterBuilder rows; empty → one blank row.
  const activeEntries = (Object.entries(filters) as [keyof ItemFilters, string][]).filter(
    ([, v]) => v && v !== ANY,
  );
  const defaultConditions: FilterCondition[] = activeEntries.map(([field, value], i) => ({
    id: `f-${field}-${i}`,
    field,
    operator: 'is',
    value,
  }));

  const apply = (conditions: FilterCondition[]) => {
    const next: ItemFilters = { ...EMPTY_FILTERS };
    for (const c of conditions) {
      if (c.value && c.field in next) next[c.field as keyof ItemFilters] = c.value;
    }
    onFiltersChange(next);
    setOpen(false);
  };

  const activeCount = activeEntries.length;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search items…"
          startSlot={<Search />}
          style={{ maxWidth: 360 }}
        />
      }
      end={
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters
                {activeCount > 0 ? (
                  <Badge tone="primary" style={{ marginLeft: 'var(--vn-space-1)' }}>
                    {activeCount}
                  </Badge>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" style={{ padding: 0, width: 'auto' }}>
              <FilterBuilder
                fields={fields}
                defaultValue={defaultConditions.length ? defaultConditions : undefined}
                onApply={apply}
                onClose={() => setOpen(false)}
              />
            </PopoverContent>
          </Popover>

          <Button variant="secondary" startIcon={<Download size={15} />} onClick={onExport} disabled={exportDisabled}>
            Export CSV
          </Button>
        </>
      }
    />
  );
}
